"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getLegacyMilestoneHeldInEscrowAmount = getLegacyMilestoneHeldInEscrowAmount;
exports.getMilestoneHeldInEscrowAmount = getMilestoneHeldInEscrowAmount;
const case_workflow_helpers_js_1 = require("../case-workflow/case-workflow.helpers.js");
const case_payment_status_helpers_js_1 = require("../case-workflow/case-payment-status.helpers.js");
function invoiceIdOf(milestone) {
    return milestone.projectInvoiceId ?? milestone.invoice?.id;
}
/** Solo paid invoices hold captured escrow (VO/DO); multi-milestone invoices stay on budget. */
function getLegacyHeldBaseAmount(milestone, projectMilestones) {
    const budget = milestone.budgetAmount || 0;
    const invoiceId = invoiceIdOf(milestone);
    const captured = milestone.invoice?.milestoneAmountCaptured;
    if (invoiceId == null || captured == null || !Number.isFinite(Number(captured))) {
        return budget;
    }
    const siblings = projectMilestones?.filter((row) => invoiceIdOf(row) === invoiceId) ??
        (milestone.project?.milestones?.filter((row) => invoiceIdOf(row) === invoiceId) || []);
    // Without sibling context, treat as solo so Progress-style VO/DO captured amounts surface.
    if (siblings.length <= 1) {
        return Math.max(0, Number(captured));
    }
    return budget;
}
/** Held-in-escrow rule for non-CASE invoices (paid, not released, minus sub-milestone releases and drawdowns). */
function getLegacyMilestoneHeldInEscrowAmount(milestone, projectMilestones) {
    if (milestone.invoice?.status !== "paid" || milestone.invoice?.instantRelease) {
        return 0;
    }
    if (milestone.invoice?.paymentMetadata?.paid_off_platform) {
        return 0;
    }
    if (milestone.paymentRequestStatus === "approved") {
        return 0;
    }
    // Mirrors the backend bucketing in money-identity.service.ts: a sub-milestone billed on its own,
    // distinct invoice never counts toward the parent's held amount (it's tracked against its own
    // invoice); a sub-milestone sharing the parent's invoice reduces the parent's held amount by its
    // full budget once its own release is approved, else by whatever has already been drawn down from it.
    let independentSubBudget = 0;
    let sharedSubReleased = 0;
    for (const sub of milestone.subMilestones ?? []) {
        if (sub.projectInvoiceId && sub.projectInvoiceId !== milestone.projectInvoiceId) {
            independentSubBudget += sub.budgetAmount || 0;
        }
        else if (sub.paymentRequestStatus === "approved") {
            sharedSubReleased += sub.budgetAmount || 0;
        }
        else {
            sharedSubReleased += sub.drawdownAmount || 0;
        }
    }
    const held = getLegacyHeldBaseAmount(milestone, projectMilestones) - independentSubBudget - sharedSubReleased - (milestone.drawdownAmount || 0);
    return Math.max(0, held);
}
/**
 * Amount still held in escrow for one milestone — the "In Escrow" figure on the project Progress tab.
 * CASE-workflow invoices use the CASE split (partial release + drawdown); other invoices use the paid /
 * approved / sub-milestone rule. Shared by business-fe and business-api (HAN-692 "Full balance in escrow").
 */
function getMilestoneHeldInEscrowAmount(milestone, invoices, projectMilestones) {
    if ((0, case_workflow_helpers_js_1.isCaseWorkflowInvoice)(milestone.invoice)) {
        return (0, case_payment_status_helpers_js_1.getCaseMilestoneHeldAfterDrawdown)(milestone, invoices ?? undefined);
    }
    return getLegacyMilestoneHeldInEscrowAmount(milestone, projectMilestones);
}
